"use client";

import React from "react";
import { Package, Search } from "lucide-react";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { cn } from "utils/utils";
import MatchedCardsIcon from "modules/common/components/Icons/MatchedCardsIcon";

export type TradeCountType = "wants" | "trades" | "match";

interface TradeCountBadgeProps {
  /** Number of items currently loaded into the view. */
  currentCount: number;
  /** Total number of items matching the active filters. Omit when the API
   *  doesn't expose one — the badge then shows the loaded count alone
   *  instead of a misleading `loaded/page-size` ratio. */
  total?: number;
  /** Drives icon and colour. Omit for a neutral ghost badge. */
  type?: TradeCountType;
  /** Renders a skeleton in place of the numbers. */
  isLoading?: boolean;
  className?: string;
}

const BADGE_COLOR: Record<TradeCountType, string> = {
  match: "badge-primary",
  trades: "badge-warning",
  wants: "badge-secondary",
};

function TypeIcon({ type }: { type?: TradeCountType }) {
  switch (type) {
    case "wants":
      return <Search size={ICON_SIZE} />;
    case "trades":
      return <Package size={ICON_SIZE} />;
    case "match":
      return <MatchedCardsIcon fontSize={ICON_SIZE} />;
    default:
      return null;
  }
}

export function TradeCountBadge({
  currentCount,
  total,
  type,
  isLoading = false,
  className,
}: TradeCountBadgeProps) {
  // Match APIs intentionally expose pagination through `hasMore`, not a total.
  // A loaded count looks like a complete total, so match views do not show a badge.
  if (type === "match") return null;

  if (!isLoading && (total ?? currentCount) === 0) return null;

  return (
    <span
      className={cn(
        "badge badge-soft badge-md shrink-0 items-center gap-1 md:gap-2",
        type ? BADGE_COLOR[type] : "badge-ghost",
        className
      )}
    >
      <TypeIcon type={type} />
      {isLoading ? (
        <span className="skeleton h-4 w-8" />
      ) : total === undefined ? (
        currentCount
      ) : (
        `${currentCount}/${total}`
      )}
    </span>
  );
}
